import { useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { ShieldCheck, AlertTriangle, RefreshCw, User } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Navigate } from "react-router-dom";

const METHOD_COLORS = {
  POST:   "brand-blue",
  PATCH:  "brand-orange",
  PUT:    "brand-orange",
  DELETE: "text-red-400",
  GET:    "text-white/50",
};

function statusColor(code) {
  if (code >= 500) return "text-red-400";
  if (code === 403 || code === 401) return "brand-orange";
  if (code >= 400) return "text-yellow-400";
  return "text-emerald-400";
}

export default function AdminAuditLog() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ user_email: "", method: "", path_prefix: "", status_min: "", status_max: "" });
  const [hoursWindow, setHoursWindow] = useState(24);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([k, v]) => v && params.set(k, v));
      params.set("limit", "200");
      const [rows, s] = await Promise.all([
        api.get(`/admin/audit-logs?${params.toString()}`),
        api.get(`/admin/audit-logs/stats?hours=${hoursWindow}`),
      ]);
      setRows(rows.data);
      setStats(s.data);
    } catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [hoursWindow]);

  // Only main admin can view audit logs
  if (user && user.role !== "admin") return <Navigate to="/admin" replace />;

  return (
    <div data-testid="admin-audit-log">
      <div className="font-mono text-xs uppercase tracking-[0.3em] brand-orange mb-3">// Governance · Audit Trail</div>
      <h1 className="font-display text-4xl font-black uppercase tracking-tighter leading-none mb-2">Audit Log.</h1>
      <p className="text-sm text-white/60 mb-8 max-w-2xl">
        Every write on the admin, parent, uploads and auth surfaces is recorded with actor, path, status and duration.
        Body payloads are captured with password fields redacted.
      </p>

      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="border border-brand-blue/30 bg-brand-blue/5 p-4" data-testid="audit-kpi-total">
            <div className="text-[10px] font-mono uppercase tracking-widest text-white/40">Actions ({hoursWindow}h)</div>
            <div className="font-display text-3xl brand-blue mt-1">{stats.total_actions}</div>
          </div>
          <div className="border border-brand-orange/30 bg-brand-orange/5 p-4" data-testid="audit-kpi-denied">
            <div className="text-[10px] font-mono uppercase tracking-widest text-white/40">RBAC denied</div>
            <div className="font-display text-3xl brand-orange mt-1">{stats.denied_403}</div>
          </div>
          <div className="border border-red-500/30 bg-red-500/5 p-4" data-testid="audit-kpi-errors">
            <div className="text-[10px] font-mono uppercase tracking-widest text-white/40">Server errors 5xx</div>
            <div className="font-display text-3xl text-red-400 mt-1">{stats.server_errors}</div>
          </div>
          <div className="border border-white/20 bg-black/20 p-4">
            <div className="text-[10px] font-mono uppercase tracking-widest text-white/40">Top actor</div>
            <div className="font-display text-lg brand-blue mt-1 truncate">{stats.top_users?.[0]?.email || "—"}</div>
            <div className="text-[10px] font-mono text-white/40">{stats.top_users?.[0]?.count || 0} actions</div>
          </div>
        </div>
      )}

      <div className="border border-white/12 bg-[#131A2B] p-4 mb-6 grid grid-cols-1 md:grid-cols-6 gap-3">
        <input placeholder="Filter by email" value={filters.user_email}
          data-testid="audit-filter-email"
          onChange={(e) => setFilters({ ...filters, user_email: e.target.value })}
          className="col-span-2 bg-[#0B1120] border border-white/20 py-2 px-3 text-sm" />
        <select value={filters.method} onChange={(e) => setFilters({ ...filters, method: e.target.value })}
          data-testid="audit-filter-method"
          className="col-span-1 bg-[#0B1120] border border-white/20 py-2 px-3 text-sm">
          <option value="">Any method</option>
          <option>POST</option><option>PATCH</option><option>PUT</option><option>DELETE</option>
        </select>
        <input placeholder="Path prefix e.g. /api/admin/blogs" value={filters.path_prefix}
          data-testid="audit-filter-path"
          onChange={(e) => setFilters({ ...filters, path_prefix: e.target.value })}
          className="col-span-2 bg-[#0B1120] border border-white/20 py-2 px-3 text-sm font-mono text-xs" />
        <button onClick={load} data-testid="audit-apply"
          className="col-span-1 bg-brand-blue text-white text-xs uppercase font-mono tracking-widest py-2 hover:glow-blue flex items-center justify-center gap-2">
          <RefreshCw size={12} /> Apply
        </button>
        <div className="col-span-6 flex items-center gap-2 pt-1">
          <span className="text-[10px] font-mono uppercase tracking-widest text-white/40">Stats window</span>
          {[1, 6, 24, 168].map((h) => (
            <button key={h} onClick={() => setHoursWindow(h)}
              className={`text-[10px] font-mono uppercase tracking-widest px-2 py-1 border ${hoursWindow === h ? "border-brand-blue brand-blue" : "border-white/20 text-white/50"}`}>
              {h < 24 ? `${h}h` : `${h/24}d`}
            </button>
          ))}
        </div>
      </div>

      <div className="border border-white/12 bg-[#131A2B]">
        <div className="grid grid-cols-12 gap-2 px-4 py-3 border-b border-white/12 text-[10px] font-mono uppercase tracking-widest text-white/40">
          <div className="col-span-2">Timestamp</div>
          <div className="col-span-1">Method</div>
          <div className="col-span-4">Path</div>
          <div className="col-span-1 text-center">Status</div>
          <div className="col-span-2">User</div>
          <div className="col-span-1">Role</div>
          <div className="col-span-1 text-right">Duration</div>
        </div>
        {loading && <div className="p-6 text-white/40 text-sm text-center">Loading…</div>}
        {!loading && rows.length === 0 && <div className="p-6 text-white/40 text-sm text-center">No audit entries match your filters.</div>}
        {rows.map((r) => (
          <div key={r._id} className="grid grid-cols-12 gap-2 px-4 py-3 border-b border-white/8 text-xs hover:bg-white/4" data-testid={`audit-row-${r._id}`}>
            <div className="col-span-2 font-mono text-white/60">{(r.ts || "").slice(0, 19).replace("T", " ")}</div>
            <div className={`col-span-1 font-mono uppercase font-bold ${METHOD_COLORS[r.method] || ""}`}>{r.method}</div>
            <div className="col-span-4 font-mono truncate">{r.path}</div>
            <div className={`col-span-1 text-center font-mono ${statusColor(r.status_code)}`}>{r.status_code}</div>
            <div className="col-span-2 truncate flex items-center gap-1">
              <User size={10} className="text-white/40 shrink-0" />
              <span className="truncate">{r.user_email || "(anonymous)"}</span>
            </div>
            <div className="col-span-1 text-[10px] font-mono uppercase text-white/50">{r.user_role || "—"}</div>
            <div className="col-span-1 text-right text-white/50 font-mono">{r.duration_ms}ms</div>
          </div>
        ))}
      </div>

      {stats && stats.top_paths?.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
          <div className="border border-white/12 bg-[#131A2B] p-5" data-testid="audit-top-users">
            <div className="font-mono text-xs uppercase tracking-widest brand-orange mb-3">// Most active users ({hoursWindow}h)</div>
            {(stats.top_users || []).map((u, i) => (
              <div key={i} className="flex justify-between text-sm py-1.5 border-b border-white/10 last:border-none">
                <span className="text-white/70 truncate">{u.email}</span>
                <span className="brand-blue font-display">{u.count}</span>
              </div>
            ))}
          </div>
          <div className="border border-white/12 bg-[#131A2B] p-5" data-testid="audit-top-paths">
            <div className="font-mono text-xs uppercase tracking-widest brand-orange mb-3">// Most-hit paths ({hoursWindow}h)</div>
            {stats.top_paths.map((p, i) => (
              <div key={i} className="flex justify-between text-xs py-1.5 border-b border-white/10 last:border-none">
                <span className="text-white/70 font-mono truncate">{p.path}</span>
                <span className="brand-orange font-display">{p.count}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
